import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import api, { formatApiErrorDetail } from "../lib/api";
import { Loader2, ShieldCheck } from "lucide-react";

export default function Login() {
  const { login, register, refresh } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingMsg, setPendingMsg] = useState("");
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [regForm, setRegForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [forgotOpen, setForgotOpen] = useState(false);
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotSent, setForgotSent] = useState(false);
  const [forgotLoading, setForgotLoading] = useState(false);

  const submitForgot = async (e) => {
    e.preventDefault();
    setForgotLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setForgotSent(true); // stessa risposta per privacy
    } finally { setForgotLoading(false); }
  };

  const go = (u) => navigate(u.role === "admin" ? "/operativa" : u.role === "struttura" ? "/strutture" : "/partner");

  const handleLogin = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await login(loginForm.email, loginForm.password);
      go(u);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const u = await register({ ...regForm, role: "partner" });
      if (u?.pending) { setPendingMsg(u.message); setRegForm({ name: "", email: "", phone: "", password: "" }); return; }
      go(u);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden lg:block">
        <img
          src="https://images.unsplash.com/photo-1619025873875-59dfdd2bbbd6?crop=entropy&cs=srgb&fm=jpg&q=85&w=940"
          alt="Ambulanza in servizio"
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-primary/80" />
        <div className="relative z-10 flex flex-col justify-between h-full p-12 text-white">
          <div className="inline-flex bg-white rounded-xl px-8 py-6 w-fit shadow-sm">
            <img src="/nexus_logo_full.png" alt="Nexus Italia Group" className="h-48 w-auto object-contain" />
          </div>
          <div>
            <h1 className="text-4xl font-bold leading-tight mb-4 font-heading">
              Gestione Trasporti Sanitari
            </h1>
            <p className="text-white/80 max-w-md text-base">
              Piattaforma operativa per la Centrale e i Partner. Assegnazione in tempo reale,
              privacy GDPR e foglio di servizio automatico.
            </p>
            <div className="mt-8 flex items-center gap-2 text-sm text-white/70">
              <ShieldCheck className="h-4 w-4" /> Dati paziente protetti fino all'assegnazione
            </div>
          </div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 bg-white">
        <div className="w-full max-w-md nx-fade-in">
          <div className="lg:hidden mb-8 flex justify-center">
            <img src="/nexus_logo.png" alt="Nexus" className="h-20 w-auto object-contain" />
          </div>
          <h2 className="text-2xl font-bold mb-1 font-heading text-slate-900">NexusOmniaCare</h2>
          <p className="text-sm text-slate-500 mb-6">Centrale Operativa e Partner Nexus Italia Group</p>

          {error && (
            <div data-testid="auth-error" className="mb-4 rounded-md bg-red-50 border border-red-200 px-4 py-2.5 text-sm text-red-700">
              {error}
            </div>
          )}
          {pendingMsg && (
            <div data-testid="auth-pending" className="mb-4 rounded-md bg-emerald-50 border border-emerald-200 px-4 py-2.5 text-sm text-emerald-800">
              {pendingMsg}
            </div>
          )}

          <Tabs defaultValue="login">
            <TabsList className="grid w-full grid-cols-2 mb-6">
              <TabsTrigger data-testid="tab-login" value="login">Accedi</TabsTrigger>
              <TabsTrigger data-testid="tab-register" value="register">Partner</TabsTrigger>
            </TabsList>

            <TabsContent value="login">
              <form onSubmit={handleLogin} className="space-y-4">
                <div>
                  <Label className="nx-label">Email</Label>
                  <Input data-testid="login-email" type="email" required className="mt-1.5"
                    value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Password</Label>
                  <Input data-testid="login-password" type="password" required className="mt-1.5"
                    value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
                </div>
                <Button data-testid="login-submit" type="submit" disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Accedi"}
                </Button>
                <button type="button" data-testid="forgot-password-link"
                  onClick={() => { setForgotOpen(true); setForgotSent(false); setForgotEmail(loginForm.email); }}
                  className="text-sm text-primary hover:underline w-full text-center mt-1">
                  Password dimenticata?
                </button>
              </form>
            </TabsContent>

            <TabsContent value="register">
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <Label className="nx-label">Ragione Sociale / Nome</Label>
                  <Input data-testid="reg-name" required className="mt-1.5"
                    value={regForm.name} onChange={(e) => setRegForm({ ...regForm, name: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Email</Label>
                  <Input data-testid="reg-email" type="email" required className="mt-1.5"
                    value={regForm.email} onChange={(e) => setRegForm({ ...regForm, email: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Telefono</Label>
                  <Input data-testid="reg-phone" className="mt-1.5"
                    value={regForm.phone} onChange={(e) => setRegForm({ ...regForm, phone: e.target.value })} />
                </div>
                <div>
                  <Label className="nx-label">Password</Label>
                  <Input data-testid="reg-password" type="password" required className="mt-1.5"
                    value={regForm.password} onChange={(e) => setRegForm({ ...regForm, password: e.target.value })} />
                </div>
                <Button data-testid="reg-submit" type="submit" disabled={loading} className="w-full">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crea account Partner"}
                </Button>
              </form>
            </TabsContent>

          </Tabs>
        </div>
      </div>

      <Dialog open={forgotOpen} onOpenChange={setForgotOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Reimposta Password</DialogTitle>
            <DialogDescription>Inserisci la tua email: ti invieremo un link per reimpostare la password.</DialogDescription>
          </DialogHeader>
          {forgotSent ? (
            <div className="py-4 text-center">
              <p className="text-sm text-slate-700">Se l'email è registrata, riceverai a breve un link per reimpostare la password.</p>
              <Button data-testid="forgot-close" className="mt-4 w-full" onClick={() => setForgotOpen(false)}>Chiudi</Button>
            </div>
          ) : (
            <form onSubmit={submitForgot} className="space-y-4 pt-2">
              <div>
                <Label className="nx-label">Email</Label>
                <Input data-testid="forgot-email" type="email" required className="mt-1.5"
                  value={forgotEmail} onChange={(e) => setForgotEmail(e.target.value)} />
              </div>
              <DialogFooter>
                <Button data-testid="forgot-submit" type="submit" disabled={forgotLoading} className="w-full gap-2">
                  {forgotLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Invia link di reimpostazione"}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
